import React, { useState } from 'react';
import { Container, Form, Button, Row, Col, Card, Tabs, Tab, Spinner, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { FaCar, FaMotorcycle, FaCheck, FaUpload } from 'react-icons/fa';

const SellVehiclePage = () => {
  const [type, setType] = useState('car');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const initialValues = {
    make: '',
    model: '',
    year: '',
    mileage: '',
    condition: '',
    price: '',
    images: [],
    // Car specific
    body_type: '',
    doors: '',
    transmission: '',
    // Motorcycle specific
    engine_size: '',
    motorcycle_type: '',
    has_sidecar: false
  };

  const validationSchema = Yup.object().shape({
    make: Yup.string().required('Make is required'),
    model: Yup.string().required('Model is required'),
    year: Yup.number()
      .required('Year is required')
      .min(1900, 'Year must be after 1900')
      .max(new Date().getFullYear() + 1, 'Year cannot be in the future'),
    mileage: Yup.number()
      .required('Mileage is required')
      .min(0, 'Mileage cannot be negative'),
    condition: Yup.string().required('Condition is required'),
    price: Yup.number()
      .required('Price is required')
      .min(0, 'Price cannot be negative'),
    ...(type === 'car' ? {
      body_type: Yup.string().required('Body type is required'),
      doors: Yup.number()
        .required('Number of doors is required')
        .min(2, 'Must have at least 2 doors')
        .max(6, 'Cannot have more than 6 doors'),
      transmission: Yup.string().required('Transmission is required')
    } : {
      engine_size: Yup.number()
        .required('Engine size is required')
        .min(50, 'Engine size must be at least 50cc')
        .max(3000, 'Engine size cannot exceed 3000cc'),
      motorcycle_type: Yup.string().required('Motorcycle type is required')
    })
  });

  const handleSubmit = async (values, { resetForm }) => {
    setSubmitting(true);
    setError(null);
    
    try {
      const vehicleData = {
        seller_id: 1, // In a real app, this would come from auth context
        make: values.make,
        model: values.model,
        year: values.year,
        mileage: values.mileage,
        vehicle_condition: values.condition,
        price: values.price,
        type: type,
        details: type === 'car' ? {
          body_type: values.body_type,
          doors: values.doors,
          transmission: values.transmission
        } : {
          engine_size: values.engine_size,
          type: values.motorcycle_type,
          has_sidecar: values.has_sidecar
        }
      };

      await axios.post('https://backend.stu89674.webhosting.arden.ac.uk/api/vehicles', vehicleData);
      setSuccess(true);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Error submitting vehicle');
    } finally {
      setSubmitting(false);
    }
  };

  const conditionOptions = ['Excellent', 'Good', 'Fair', 'Poor', 'Salvage'];
  const bodyTypes = ['Sedan', 'Coupe', 'SUV', 'Truck', 'Van', 'Hatchback', 'Convertible', 'Wagon'];
  const transmissions = ['Automatic', 'Manual', 'CVT', 'Dual-Clutch'];
  const motorcycleTypes = ['Cruiser', 'Sport', 'Touring', 'Dual-Sport', 'Scooter', 'Off-Road'];

  return (
    <Container className="py-4">
      <h1 className="mb-4">Sell Your Vehicle</h1>
      
      <Card>
        <Card.Body>
          {success && (
            <Alert variant="success" onClose={() => setSuccess(false)} dismissible>
              <FaCheck className="me-2" />
              Your vehicle has been listed successfully!
            </Alert>
          )}
          
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          <Tabs
            activeKey={type}
            onSelect={(k) => {
              setType(k);
              setSuccess(false);
              setError(null);
            }}
            className="mb-4"
          >
            <Tab eventKey="car" title={<><FaCar className="me-2" />Car</>}>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                  <Form onSubmit={handleSubmit}>
                    <h4 className="mt-3 mb-4">Vehicle Information</h4>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="make">
                          <Form.Label>Make</Form.Label>
                          <Form.Control
                            type="text"
                            name="make"
                            value={values.make}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.make && !!errors.make}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.make}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="model">
                          <Form.Label>Model</Form.Label>
                          <Form.Control
                            type="text"
                            name="model"
                            value={values.model}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.model && !!errors.model}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.model}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="year">
                          <Form.Label>Year</Form.Label>
                          <Form.Control
                            type="number"
                            name="year"
                            value={values.year}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.year && !!errors.year}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.year}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="mileage">
                          <Form.Label>Mileage</Form.Label>
                          <Form.Control
                            type="number"
                            name="mileage"
                            value={values.mileage}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.mileage && !!errors.mileage}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.mileage}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="condition">
                          <Form.Label>Condition</Form.Label>
                          <Form.Select
                            name="condition"
                            value={values.condition}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.condition && !!errors.condition}
                          >
                            <option value="">Select condition</option>
                            {conditionOptions.map(cond => (
                              <option key={cond} value={cond.toLowerCase()}>{cond}</option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.condition}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="price">
                          <Form.Label>Price ($)</Form.Label>
                          <Form.Control
                            type="number"
                            name="price"
                            value={values.price}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.price && !!errors.price}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.price}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <h4 className="mt-4 mb-4">Car Details</h4>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="body_type">
                          <Form.Label>Body Type</Form.Label>
                          <Form.Select
                            name="body_type"
                            value={values.body_type}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.body_type && !!errors.body_type}
                          >
                            <option value="">Select body type</option>
                            {bodyTypes.map(type => (
                              <option key={type} value={type.toLowerCase()}>{type}</option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.body_type}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="doors">
                          <Form.Label>Number of Doors</Form.Label>
                          <Form.Control
                            type="number"
                            name="doors"
                            value={values.doors}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.doors && !!errors.doors}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.doors}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Group controlId="transmission">
                          <Form.Label>Transmission</Form.Label>
                          <Form.Select
                            name="transmission"
                            value={values.transmission}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.transmission && !!errors.transmission}
                          >
                            <option value="">Select transmission</option>
                            {transmissions.map(trans => (
                              <option key={trans} value={trans.toLowerCase()}>{trans}</option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.transmission}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <h4 className="mt-4 mb-4">Photos</h4>
                    <Form.Group controlId="images" className="mb-4">
                      <Form.Label>Upload Vehicle Images</Form.Label>
                      <div className="border rounded p-4 text-center">
                        <FaUpload className="display-4 text-muted mb-3" />
                        <p>Drag and drop images here or click to browse</p>
                        <Form.Control
                          type="file"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files);
                            setFieldValue('images', files);
                          }}
                        />
                      </div>
                    </Form.Group>

                    <div className="d-grid">
                      <Button 
                        variant="primary" 
                        type="submit" 
                        size="lg"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Listing Vehicle...
                          </>
                        ) : 'List Vehicle'}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Tab>

            <Tab eventKey="motorcycle" title={<><FaMotorcycle className="me-2" />Motorcycle</>}>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                  <Form onSubmit={handleSubmit}>
                    <h4 className="mt-3 mb-4">Vehicle Information</h4>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="make">
                          <Form.Label>Make</Form.Label>
                          <Form.Control
                            type="text"
                            name="make"
                            value={values.make}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.make && !!errors.make}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.make}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="model">
                          <Form.Label>Model</Form.Label>
                          <Form.Control
                            type="text"
                            name="model"
                            value={values.model}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.model && !!errors.model}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.model}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="year">
                          <Form.Label>Year</Form.Label>
                          <Form.Control
                            type="number"
                            name="year"
                            value={values.year}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.year && !!errors.year}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.year}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="mileage">
                          <Form.Label>Mileage</Form.Label>
                          <Form.Control
                            type="number"
                            name="mileage"
                            value={values.mileage}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.mileage && !!errors.mileage}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.mileage}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="condition">
                          <Form.Label>Condition</Form.Label>
                          <Form.Select
                            name="condition"
                            value={values.condition}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.condition && !!errors.condition}
                          >
                            <option value="">Select condition</option>
                            {conditionOptions.map(cond => (
                              <option key={cond} value={cond.toLowerCase()}>{cond}</option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.condition}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="price">
                          <Form.Label>Price ($)</Form.Label>
                          <Form.Control
                            type="number"
                            name="price"
                            value={values.price}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.price && !!errors.price}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.price}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <h4 className="mt-4 mb-4">Motorcycle Details</h4>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="engine_size">
                          <Form.Label>Engine Size (cc)</Form.Label>
                          <Form.Control
                            type="number"
                            name="engine_size"
                            value={values.engine_size}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.engine_size && !!errors.engine_size}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.engine_size}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="motorcycle_type">
                          <Form.Label>Type</Form.Label>
                          <Form.Select
                            name="motorcycle_type"
                            value={values.motorcycle_type}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.motorcycle_type && !!errors.motorcycle_type}
                          >
                            <option value="">Select type</option>
                            {motorcycleTypes.map(type => (
                              <option key={type} value={type.toLowerCase()}>{type}</option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.motorcycle_type}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group controlId="has_sidecar" className="mb-4">
                      <Form.Check
                        type="checkbox"
                        label="Has sidecar"
                        name="has_sidecar"
                        checked={values.has_sidecar}
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <h4 className="mt-4 mb-4">Photos</h4>
                    <Form.Group controlId="images" className="mb-4">
                      <Form.Label>Upload Vehicle Images</Form.Label>
                      <div className="border rounded p-4 text-center">
                        <FaUpload className="display-4 text-muted mb-3" />
                        <p>Drag and drop images here or click to browse</p>
                        <Form.Control
                          type="file"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files);
                            setFieldValue('images', files);
                          }}
                        />
                      </div>
                    </Form.Group>

                    <div className="d-grid">
                      <Button 
                        variant="primary" 
                        type="submit" 
                        size="lg"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Listing Vehicle...
                          </>
                        ) : 'List Vehicle'}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SellVehiclePage;